import { generateFakeContacts, createFakeContact } from './fakeData/contactsData';
import { generateFakeCompanies, createFakeCompany } from './fakeData/companiesData';
import { generateFakeServices, createFakeService } from './fakeData/servicesData';
import { generateFakeVenues, createFakeVenue } from './fakeData/venuesData';

// Default counts for generating collections
const DEFAULT_CONTACT_COUNT = 25;
const DEFAULT_COMPANY_COUNT = 15;
const DEFAULT_SERVICE_COUNT = 12;
const DEFAULT_VENUE_COUNT = 10;

// Cache for generated data to maintain consistency across calls
let cachedContacts = null;
let cachedCompanies = null;
let cachedServices = null;
let cachedVenues = null;

/**
 * Get a collection of fake contacts
 * @param {Number} count - Number of contacts to generate
 * @param {Boolean} useCache - Whether to use cached data or generate new
 * @returns {Array} Array of contact objects
 */
export const getFakeContacts = (count = DEFAULT_CONTACT_COUNT, useCache = true) => {
    if (useCache && cachedContacts) {
        return count === DEFAULT_CONTACT_COUNT
            ? cachedContacts
            : cachedContacts.slice(0, count);
    }

    cachedContacts = generateFakeContacts(count);
    return cachedContacts;
};

/**
 * Get a single fake contact by ID
 * @param {String|Number} id - ID of the contact to retrieve
 * @returns {Object} Contact object
 */
export const getFakeContactById = (id) => {
    // Ensure contacts are generated
    const contacts = getFakeContacts();

    // Try to find by ID in cache
    const contact = contacts.find(c => c.id.toString() === id.toString());

    // Return found contact or generate a new one with the specified ID
    return contact || createFakeContact(id);
};

/**
 * Get a collection of fake companies
 * @param {Number} count - Number of companies to generate
 * @param {Boolean} useCache - Whether to use cached data or generate new
 * @returns {Array} Array of company objects
 */
export const getFakeCompanies = (count = DEFAULT_COMPANY_COUNT, useCache = true) => {
    if (useCache && cachedCompanies) {
        return count === DEFAULT_COMPANY_COUNT
            ? cachedCompanies
            : cachedCompanies.slice(0, count);
    }

    cachedCompanies = generateFakeCompanies(count);
    return cachedCompanies;
};

/**
 * Get a single fake company by ID
 * @param {String|Number} id - ID of the company to retrieve
 * @returns {Object} Company object
 */
export const getFakeCompanyById = (id) => {
    // Ensure companies are generated
    const companies = getFakeCompanies();

    // Try to find by ID in cache
    const company = companies.find(c => c.id.toString() === id.toString());

    // Return found company or generate a new one with the specified ID
    return company || createFakeCompany(id);
};

/**
 * Get a collection of fake services
 * @param {Number} count - Number of services to generate
 * @param {Boolean} useCache - Whether to use cached data or generate new
 * @returns {Array} Array of service objects
 */
export const getFakeServices = (count = DEFAULT_SERVICE_COUNT, useCache = true) => {
    if (useCache && cachedServices) {
        return count === DEFAULT_SERVICE_COUNT
            ? cachedServices
            : cachedServices.slice(0, count);
    }

    cachedServices = generateFakeServices(count);
    return cachedServices;
};

/**
 * Get a single fake service by ID
 * @param {String|Number} id - ID of the service to retrieve
 * @returns {Object} Service object
 */
export const getFakeServiceById = (id) => {
    // Ensure services are generated
    const services = getFakeServices();

    // Try to find by ID in cache
    const service = services.find(s => s.id.toString() === id.toString());

    // Return found service or generate a new one with the specified ID
    return service || createFakeService(id);
};

/**
 * Get contacts for a specific company
 * @param {String|Number} companyId - ID of the company to get contacts for
 * @param {Number} count - Maximum number of contacts to return
 * @returns {Array} Array of contact objects associated with the company
 */
export const getContactsForCompany = (companyId, count = 5) => {
    const company = getFakeCompanyById(companyId);
    return company.staff || generateFakeContacts(count).map(contact => {
        contact.company = company.name;
        return contact;
    });
};

/**
 * Get a collection of fake venues
 * @param {Number} count - Number of venues to generate
 * @param {Boolean} useCache - Whether to use cached data or generate new
 * @returns {Array} Array of venue objects
 */
export const getFakeVenues = (count = DEFAULT_VENUE_COUNT, useCache = true) => {
    if (useCache && cachedVenues) {
        return count === DEFAULT_VENUE_COUNT
            ? cachedVenues
            : cachedVenues.slice(0, count);
    }

    cachedVenues = generateFakeVenues(count);
    return cachedVenues;
};

/**
 * Get a single fake venue by ID
 * @param {String|Number} id - ID of the venue to retrieve
 * @returns {Object} Venue object
 */
export const getFakeVenueById = (id) => {
    // Ensure venues are generated
    const venues = getFakeVenues();

    // Try to find by ID in cache
    const venue = venues.find(v => v.id.toString() === id.toString());

    // Return found venue or generate a new one with the specified ID
    return venue || createFakeVenue(id);
};

/**
 * Get staff contacts for a specific venue
 * @param {String|Number} venueId - ID of the venue to get staff for
 * @param {Number} count - Maximum number of staff to return if new staff needs to be generated
 * @returns {Array} Array of contact objects associated with the venue
 */
export const getStaffForVenue = (venueId, count = 3) => {
    const venue = getFakeVenueById(venueId);
    return venue.staff || generateFakeContacts(count).map(contact => {
        contact.company = venue.name;
        contact.jobTitle = `${venue.name} Staff`;
        return contact;
    });
};

/**
 * Clear the data cache to force regeneration
 */
export const clearFakeDataCache = () => {
    cachedContacts = null;
    cachedCompanies = null;
    cachedServices = null;
    cachedVenues = null;
}; 